import { Card } from 'components/Card';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  doSetupBillingAdminUpdateProduct,
  eventSelectScreenSelection,
} from 'store/actions';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { cloneDeep, isEqual, sortBy, toInteger } from 'lodash';
import { useSelector } from 'hooks/useSelector';
import {
  selectBillingProduct,
  selectRemoteCustomers,
  selectRemoteOrganisationId,
} from 'store/remote/selectors';
import { BillingAccountEnum, BillingProduct } from 'types/Billing';
import { MonthEnum, Months } from 'types/types';
import { TextField, TextFieldSpecial } from 'components/Field/TextField';
import { CardRow } from 'components/CardRow';
import { useAlert } from 'hooks/useAlert';
import { randomKey } from 'lib/riskbase';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { ICONS } from 'components/Icon';
import { theme } from 'lib/theme';

const DefaultProduct: BillingProduct = {
  id: '',
  name: '',
  cost: 0,
  vat: 20,
  invoiceCode: '',
  account: BillingAccountEnum.licensing,
  invoiceToOrganisationId: null,
  months: Months.map((m) => m.name),
};

export const BillingProductModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsBillingProduct>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const forceUpdate = useForceUpdate();
  const confirmUnsaved = useConfirmUnsaved();

  const { productId } = route.params;

  const eventKeyOrganisationRef = React.useRef(randomKey());

  const product =
    useSelector((state) => selectBillingProduct(state, productId || '-1')) ||
    DefaultProduct;

  const customers = useSelector(selectRemoteCustomers) || {};
  const organisationId = useSelector(selectRemoteOrganisationId);
  const dataRef = React.useRef<BillingProduct>(cloneDeep(product));
  const isDirty = React.useCallback(
    () => !isEqual(product, dataRef.current),
    [product],
  );
  const isDirtyRef = React.useRef<boolean>(false);
  isDirtyRef.current = isDirty();

  const invoiceTo = customers[organisationId || '-1'];
  const invoiceToOverride =
    customers[dataRef.current.invoiceToOrganisationId || '-1'];

  const handleSave = React.useCallback(() => {
    const errors: string[] = [];
    if (!dataRef.current.name.trim()) {
      errors.push('Name is required');
    }
    if (!dataRef.current.cost) {
      errors.push('Cost is required and must be more than £0.00');
    }
    if (!dataRef.current.months.length) {
      errors.push('At least one month is required');
    }
    if (errors.length) {
      alert({
        text: errors.join('. '),
      });
      return;
    }
    dispatch(doSetupBillingAdminUpdateProduct(dataRef.current));
  }, [alert, dispatch]);

  const handleClose = React.useCallback(() => {
    if (isDirty()) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () =>
          dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SettingsBillingProduct])),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SettingsBillingProduct]));
  }, [confirmUnsaved, dispatch, handleSave, isDirty]);

  const handleChangeValue = <K extends keyof typeof dataRef.current>(
    key: K,
    value: (typeof dataRef.current)[K],
    doForceUpdate: boolean = false,
  ) => {
    dataRef.current[key] = value;
    if (doForceUpdate || isDirtyRef.current !== isDirty()) {
      forceUpdate();
    }
  };

  const toggleMonth = (month: MonthEnum) => {
    if (dataRef.current.months.includes(month)) {
      dataRef.current.months = dataRef.current.months.filter(
        (m) => m !== month,
      );
    } else {
      const newMonthArray = [...dataRef.current.months, month];
      dataRef.current.months = Months.filter((m) =>
        newMonthArray.includes(m.name),
      ).map((m) => m.name);
    }
    forceUpdate();
  };

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty() });
  }, [navigation, isDirty]);
  useEsc(handleClose);

  React.useEffect(() => {
    const events = [
      doAddEvent(eventSelectScreenSelection, (action) => {
        if (action.payload.key === eventKeyOrganisationRef.current) {
          dataRef.current.invoiceToOrganisationId =
            action.payload.stringValues[0] || null;
          forceUpdate();
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [forceUpdate]);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading="Product"
          cancelButtonText="Close"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      <Card explanation="Use invoice codes to split items into different invoices">
        <TextField
          placeholder="Product Name"
          onChange={(value) => handleChangeValue('name', value)}
          value={dataRef.current.name}
          onEsc={handleClose}
          autoFocus={!dataRef.current.id}
          autoFocusWeb
          autoFocusDelay
        />
        <TextField
          textLeft="Invoice Code"
          placeholder="default"
          onChange={(value) => handleChangeValue('invoiceCode', value)}
          value={
            dataRef.current.invoiceCode !== 'default'
              ? dataRef.current.invoiceCode
              : ''
          }
          onEsc={handleClose}
        />
      </Card>

      <Card
        label="Cost Per Month (excl. VAT)"
        contentStyle={{ flexDirection: 'row', flexWrap: 'wrap' }}
        marginBottomQuarter
      >
        <TextField
          placeholder="0.00"
          onChange={(value) =>
            handleChangeValue('cost', toInteger(value || '0'))
          }
          textLeft="£"
          indentActions
          value={`${dataRef.current.cost}`}
          special={TextFieldSpecial.currency}
          onEsc={handleClose}
          containerStyle={{ flex: 1 }}
        />
        <CardRow
          placeholder={`${dataRef.current.vat}% VAT`}
          style={{ backgroundColor: theme.colors.white }}
        />
        {/* <TextField
          disabled
          placeholder="0"
          onChange={(value) =>
            handleChangeValue('vat', toInteger(value || '0'))
          }
          textRight="% VAT"
          indentActions
          value={`${dataRef.current.vat}`}
          special={TextFieldSpecial.number}
          maxNumberValue={100}
          onEsc={handleClose}
          inputStyle={{ textAlign: 'right', paddingRight: 6 }}
          containerStyle={{ width: 100, paddingRight: theme.paddingHorizontal }}
        /> */}
      </Card>

      <Card
        contentStyle={{ flexDirection: 'row', flexWrap: 'wrap' }}
        marginBottomQuarter
      >
        <CardRow
          label="Licensing"
          style={{ width: '50%' }}
          radioValue={dataRef.current.account === BillingAccountEnum.licensing}
          onPress={() =>
            handleChangeValue('account', BillingAccountEnum.licensing, true)
          }
        />
        <CardRow
          label="Rebilling"
          style={{ width: '50%' }}
          radioValue={dataRef.current.account === BillingAccountEnum.rebilling}
          onPress={() =>
            handleChangeValue('account', BillingAccountEnum.rebilling, true)
          }
        />
      </Card>

      <Card contentStyle={{ flexDirection: 'row', flexWrap: 'wrap' }}>
        {Months.map((m) => (
          <CardRow
            key={m.number}
            label={m.name}
            checkedValue={dataRef.current.months.includes(m.name)}
            onPress={() => toggleMonth(m.name)}
            style={{ width: '33.3%' }}
          />
        ))}
      </Card>

      <Card label="Invoice To">
        <CardRow
          value={invoiceToOverride?.name || ''}
          placeholder={invoiceTo?.name || ''}
          actionIcon={ICONS.CircleXmarkLight}
          onAction={
            invoiceToOverride
              ? () => handleChangeValue('invoiceToOrganisationId', null, true)
              : undefined
          }
          onPress={() =>
            dispatch(
              NAVIGATE_MODAL({
                screen: ModalScreens.SELECT,
                params: {
                  title: 'Organisations',
                  eventKey: eventKeyOrganisationRef.current,
                  searchable: true,
                  options: sortBy(Object.values(customers), 'name').map(
                    (c) => ({
                      name: c.name,
                      internalValue: c.id,
                    }),
                  ),
                },
              }),
            )
          }
        />
      </Card>
    </PageScrollView>
  );
};
